/**
 * TopStep Command Center
 *
 * Integrated dashboard for the $50k Topstep Challenge:
 *   Tab 0 — Overview    : Auth, account, challenge progress, risk engine
 *   Tab 1 — Autopilot   : Tomi+Kaze autonomous futures trading
 *   Tab 2 — Kaze Signals: Futures-scoped market intelligence & news
 *   Tab 3 — Chart       : Live OHLCV chart via ProjectX bar history
 *   Tab 4 — Orders      : Manual order entry, fill history, cancel
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  Stop,
  CheckCircle,
  ShowChart,
  MonitorHeart,
  CurrencyExchange,
  Cancel,
  Send,
  Lock,
  LockOpen,
  Air,
} from '@mui/icons-material';
import topstepService, {
  TopStepSession,
  TopStepAccount,
  TopStepContract,
  TopStepRiskState,
  ChallengeProgress,
  AutopilotStatus,
  KazeFuturesSignal,
  KazeSignalsMeta,
  TopStepInstrumentMeta,
  TopStepOrder,
  TopStepPosition,
} from '../services/topstepService';
import { createTopStepDatafeed, normalizeTopStepSymbolKey, registerContract } from '../utils/topstepDatafeed';
import FibonacciLadderViz, { FibLadderPosition } from '../components/topstep/FibonacciLadderViz';

// ─── Constants ───────────────────────────────────────────

const COLORS = {
  gold:     '#D4AF37',
  platinum: '#E5E4E2',
  blue:     '#4FC3F7',
  green:    '#66BB6A',
  red:      '#EF5350',
  orange:   '#FFA726',
  surface:  '#161B27',
  surface2: '#1E2535',
  border:   'rgba(255,255,255,0.07)',
  muted:    'rgba(255,255,255,0.45)',
};

const CHALLENGE = {
  target:      3000,
  hardMax:     2000,
  internalStop: 1700,
  dailyCap:    600,
  account:     50000,
};

const DEFAULT_AP_INSTRUMENTS = ['MNQ', 'MES', 'MGC', 'M2K', 'MYM'];
const INSTRUMENT_GROUP_ORDER = ['Micro Equity', 'Full Equity', 'Metals / Energy', 'FX', 'Rates', 'Other'];

const FALLBACK_INSTRUMENT_OPTIONS: TopStepInstrumentMeta[] = [
  { symbol: 'MNQ', fullName: 'Micro Nasdaq-100', tickSize: 0.25, tickValue: 0.50, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'MES', fullName: 'Micro S&P 500', tickSize: 0.25, tickValue: 1.25, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'MGC', fullName: 'Micro Gold', tickSize: 0.10, tickValue: 1.00, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'M2K', fullName: 'Micro Russell 2000', tickSize: 0.10, tickValue: 0.50, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
  { symbol: 'MYM', fullName: 'Micro Dow Jones', tickSize: 1, tickValue: 0.50, stopTicks: 0, targetTicks: 0, riskPerContract: 0, marginPerContract: 0, isDefault: true, isFullSize: false },
];

function instrumentGroup(option: TopStepInstrumentMeta): string {
  if (['MNQ', 'MES', 'MYM', 'M2K'].includes(option.symbol)) return 'Micro Equity';
  if (['NQ', 'ES', 'RTY', 'NKD'].includes(option.symbol)) return 'Full Equity';
  if (['MGC', 'GC', 'MCL', 'CL', 'SI', 'NG', 'HG'].includes(option.symbol)) return 'Metals / Energy';
  if (option.symbol === 'M6E' || option.symbol.startsWith('6')) return 'FX';
  if (['ZT', 'ZN', 'ZB'].includes(option.symbol)) return 'Rates';
  return 'Other';
}

// ─── Helpers ─────────────────────────────────────────────

interface TabPanelProps { children: React.ReactNode; value: number; index: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null;
}

function fmt$(n: number, decimals = 0) {
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function pct(n: number, total: number) {
  if (!total) return '0.0%';
  return `${((n / total) * 100).toFixed(1)}%`;
}

function statusColor(status: number) {
  if (status === 2) return COLORS.green;
  if (status === 3 || status === 4 || status === 5) return COLORS.red;
  if (status === 1) return COLORS.blue;
  return COLORS.muted;
}

function statusLabel(status: number) {
  const labels: Record<number, string> = { 0: 'Pending', 1: 'Working', 2: 'Filled', 3: 'Cancelled', 4: 'Rejected', 5: 'Expired' };
  return labels[status] ?? `Status ${status}`;
}

function orderSideLabel(side: number) { return side === 0 ? 'BUY' : 'SELL'; }
// ProjectX: type 1=Limit, type 2=Market, type 3=Stop, type 4=StopLimit
function orderTypeLabel(type: number) {
  const labels: Record<number, string> = { 1: 'Limit', 2: 'Market', 3: 'Stop', 4: 'StopLmt' };
  return labels[type] ?? `Type${type}`;
}
function fmtPrice(price: number | undefined): string {
  if (price == null) return '—';
  // Use 4 decimals for FX/small prices, 2 for large prices (indices, metals)
  return price < 100 ? price.toFixed(4) : price.toFixed(2);
}

function directionColor(d: string) {
  if (d === 'LONG' || d === 'BULLISH') return COLORS.green;
  if (d === 'SHORT' || d === 'BEARISH') return COLORS.red;
  return COLORS.muted;
}

function scoreBar(score: number) {
  const pct = Math.round(score * 100);
  const color = pct >= 70 ? COLORS.green : pct <= 30 ? COLORS.red : COLORS.orange;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }}
      />
      <Typography variant="caption" sx={{ color, fontWeight: 700, minWidth: 36, textAlign: 'right' }}>
        {pct}%
      </Typography>
    </Box>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ p: 2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color: color || COLORS.platinum, mt: 0.25 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: COLORS.muted }}>{sub}</Typography>}
    </Box>
  );
}

function EquityCurve({ trades }: { trades: TopStepRiskState['tradeLog'] }) {
  const closed = [...trades]
    .filter(t => t.status === 'closed' && t.realizedPnl != null && t.closedAt)
    .sort((a, b) => new Date(a.closedAt!).getTime() - new Date(b.closedAt!).getTime());

  if (closed.length < 2) {
    return (
      <Box sx={{ height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" sx={{ color: COLORS.muted }}>Not enough closed trades to plot curve</Typography>
      </Box>
    );
  }

  const points: number[] = [0];
  closed.forEach(t => points.push(points[points.length - 1] + (t.realizedPnl ?? 0)));

  const W = 600, H = 72, PAD = 4;
  const minY = Math.min(...points);
  const maxY = Math.max(...points);
  const range = maxY - minY || 1;

  const toX = (i: number) => PAD + (i / (points.length - 1)) * (W - PAD * 2);
  const toY = (v: number) => H - PAD - ((v - minY) / range) * (H - PAD * 2);

  const linePath = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${toX(points.length - 1).toFixed(1)},${H} L${toX(0).toFixed(1)},${H} Z`;

  const finalVal = points[points.length - 1];
  const isPositive = finalVal >= 0;
  const lineColor = isPositive ? '#66BB6A' : '#EF5350';
  const fillColor = isPositive ? 'rgba(102,187,106,0.12)' : 'rgba(239,83,80,0.10)';

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <path d={areaPath} fill={fillColor} />
        <path d={linePath} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinejoin="round" />
        {minY < 0 && maxY > 0 && (
          <line x1={PAD} y1={toY(0).toFixed(1)} x2={W - PAD} y2={toY(0).toFixed(1)}
            stroke="rgba(255,255,255,0.18)" strokeWidth="1" strokeDasharray="4 3" />
        )}
      </svg>
    </Box>
  );
}

// ─── Page ────────────────────────────────────────────────

export default function TopStepCommandCenterPage() {
  const [tab, setTab] = useState(0);

  // Auth / account state
  const [session, setSession] = useState<TopStepSession | null>(null);
  const [account, setAccount] = useState<TopStepAccount | null>(null);
  const [contracts, setContracts] = useState<TopStepContract[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Risk / challenge
  const [risk, setRisk] = useState<TopStepRiskState | null>(null);
  const [progress, setProgress] = useState<ChallengeProgress | null>(null);
  const [killConfirm, setKillConfirm] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);

  // Autopilot
  const [apStatus, setApStatus] = useState<AutopilotStatus | null>(null);
  const [apInstruments, setApInstruments] = useState<string[]>(DEFAULT_AP_INSTRUMENTS);
  const [apInstrumentOptions, setApInstrumentOptions] = useState<TopStepInstrumentMeta[]>(FALLBACK_INSTRUMENT_OPTIONS);
  const [apLoading, setApLoading] = useState(false);

  // Kaze signals
  const [kazeSignals, setKazeSignals] = useState<KazeFuturesSignal[]>([]);
  const [kazeMeta, setKazeMeta] = useState<KazeSignalsMeta | null>(null);
  const [kazeLoading, setKazeLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Chart
  const chartRef = useRef<HTMLDivElement | null>(null);
  const tvRef = useRef<any>(null);
  const [chartContract, setChartContract] = useState<string>('');

  // Orders
  const [orders, setOrders] = useState<TopStepOrder[]>([]);
  const [positions, setPositions] = useState<TopStepPosition[]>([]);
  const [orderForm, setOrderForm] = useState({ symbol: 'MNQ', side: 'BUY', contracts: 1, type: 'market', limitPrice: '' });
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // ── Fibonacci Ladder HWM tracker (client-side) ─────────
  const posHwmRef = useRef<Record<string, number>>({});

  const fibLadderPositions: FibLadderPosition[] = React.useMemo(() => {
    return positions.map(p => {
      const sym = (p.contractName || '').replace(/[FGHJKMNQUVXZ]\d{1,2}$/i, '').toUpperCase();
      const pnl = p.unrealizedPnl ?? 0;
      const prevHwm = posHwmRef.current[p.contractId] ?? pnl;
      const hwm = Math.max(prevHwm, pnl);
      posHwmRef.current[p.contractId] = hwm;
      const effectiveStop = hwm >= 400 ? hwm - 100
                          : hwm >= 300 ? hwm - 120
                          : hwm >= 200 ? 100
                          : hwm >= 150 ? 25
                          : -200;
      return {
        symbol: sym,
        size: p.size,
        pnl,
        hwm,
        effectiveStop,
        entryPrice: p.avgEntryPrice,
        side: p.side === 0 ? 'long' as const : 'short' as const,
      };
    });
  }, [positions]);

  const apInstrumentGroups = React.useMemo(() => {
    const grouped = apInstrumentOptions.reduce<Record<string, TopStepInstrumentMeta[]>>((acc, option) => {
      const group = instrumentGroup(option);
      acc[group] = acc[group] || [];
      acc[group].push(option);
      return acc;
    }, {});

    return INSTRUMENT_GROUP_ORDER
      .filter(group => (grouped[group]?.length || 0) > 0)
      .map(group => ({ label: group, items: grouped[group].sort((a, b) => a.symbol.localeCompare(b.symbol)) }));
  }, [apInstrumentOptions]);

  // ── Init & polling ─────────────────────────────────────

  const loadSessionState = useCallback(async () => {
    try {
      const s = await topstepService.getAuthStatus();
      setSession(s);
      if (s.authenticated) {
        const [acct, riskData, prog, ap] = await Promise.allSettled([
          topstepService.getAccount(),
          topstepService.getRisk(),
          topstepService.getChallengeProgress(),
          topstepService.getAutopilotStatus(),
        ]);
        if (acct.status === 'fulfilled') setAccount(acct.value);
        if (riskData.status === 'fulfilled') setRisk(riskData.value);
        if (prog.status === 'fulfilled') setProgress(prog.value);
        if (ap.status === 'fulfilled') setApStatus(ap.value);
      }
    } catch { /* noop */ }
  }, []);

  const loadContracts = useCallback(async () => {
    try {
      const { contracts: c, registry } = await topstepService.getContracts();
      setContracts(c);
      // Register with TV datafeed and default chart symbol
      c.forEach(ct => {
        const info = { contractId: ct.id, name: ct.name, description: ct.description, tickSize: ct.tickSize, tickValue: ct.tickValue, symbolId: ct.symbolId };
        [ct.symbolId, ct.name, normalizeTopStepSymbolKey(ct.name), normalizeTopStepSymbolKey(ct.symbolId)]
          .filter(Boolean)
          .forEach(alias => registerContract(String(alias), info));
      });
      registry?.forEach(entry => {
        registerContract(entry.symbol, { contractId: entry.id, name: entry.name, description: entry.name, tickSize: entry.tickSize, tickValue: entry.tickValue });
      });
      if (c.length > 0 && !chartContract) {
        setChartContract(normalizeTopStepSymbolKey(c[0].name || c[0].symbolId));
      }
    } catch { /* noop */ }
  }, [chartContract]);

  const loadInstrumentOptions = useCallback(async () => {
    try {
      const registry = await topstepService.getInstruments();
      const options = registry.instruments || [];
      if (options.length === 0) return;

      const available = new Set(options.map(option => option.symbol));
      const defaults = (registry.defaults.length ? registry.defaults : options.filter(option => option.isDefault).map(option => option.symbol))
        .filter(symbol => available.has(symbol));

      setApInstrumentOptions(options);
      setApInstruments(prev => {
        const stillValid = prev.filter(symbol => available.has(symbol));
        const isInitialDefault = prev.length === DEFAULT_AP_INSTRUMENTS.length
          && DEFAULT_AP_INSTRUMENTS.every(symbol => prev.includes(symbol));
        if (stillValid.length === 0 || isInitialDefault) return defaults.length > 0 ? defaults : stillValid;
        return stillValid;
      });
    } catch { /* noop */ }
  }, []);

  const loadOrders = useCallback(async () => {
    const [ord, pos] = await Promise.allSettled([
      topstepService.getOrders(),
      topstepService.getPositions(),
    ]);
    if (ord.status === 'fulfilled') setOrders(ord.value);
    if (pos.status === 'fulfilled') setPositions(pos.value.positions);
  }, []);

  const loadKazeSignals = useCallback(async () => {
    if (!contracts.length) return;
    setKazeLoading(true);
    try {
      const [sigResult, metaResult] = await Promise.allSettled([
        topstepService.getKazeFuturesSignals(contracts),
        topstepService.getKazeSignalsMeta(),
      ]);
      if (sigResult.status === 'fulfilled') setKazeSignals(sigResult.value);
      if (metaResult.status === 'fulfilled') setKazeMeta(metaResult.value);
    } finally {
      setKazeLoading(false);
    }
  }, [contracts]);

  useEffect(() => { loadSessionState(); }, [loadSessionState]);

  useEffect(() => {
    if (session?.authenticated) {
      loadContracts();
      loadInstrumentOptions();
    }
  }, [session?.authenticated, loadContracts, loadInstrumentOptions]);

  useEffect(() => {
    if (contracts.length > 0) loadKazeSignals();
  }, [contracts, loadKazeSignals]);

  useEffect(() => {
    if (tab === 1 || tab === 4) loadOrders();
  }, [tab, loadOrders]);

  // Auto-poll autopilot status every 30s
  useEffect(() => {
    if (!session?.authenticated) return;
    const id = setInterval(async () => {
      try {
        const [ap, riskData, prog, pos] = await Promise.allSettled([
          topstepService.getAutopilotStatus(),
          topstepService.getRisk(),
          topstepService.getChallengeProgress(),
          topstepService.getPositions(),
        ]);
        if (ap.status === 'fulfilled') setApStatus(ap.value);
        if (riskData.status === 'fulfilled') setRisk(riskData.value);
        if (prog.status === 'fulfilled') setProgress(prog.value);
        if (pos.status === 'fulfilled') setPositions(pos.value.positions);
      } catch { /* noop */ }
    }, 30000);
    return () => clearInterval(id);
  }, [session?.authenticated]);

  // ── Chart init ──────────────────────────────────────────

  useEffect(() => {
    if (tab !== 3 || !chartContract) return;
    let mounted = true;

    const initChart = async () => {
      if (!chartRef.current) return;

      if (!(window as any).TradingView) {
        try {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/charting_library/charting_library.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('TradingView library not found'));
            document.head.appendChild(script);
          });
        } catch { return; }
      }

      if (!mounted || !(window as any).TradingView) return;
      if (tvRef.current) { try { tvRef.current.remove(); } catch { /**/ } tvRef.current = null; }

      const containerId = `ts_chart_${Date.now()}`;
      chartRef.current.innerHTML = '';
      const div = document.createElement('div');
      div.id = containerId;
      div.style.cssText = 'width:100%;height:520px;';
      chartRef.current.appendChild(div);

      requestAnimationFrame(() => {
        if (!mounted || !document.getElementById(containerId)) return;
        try {
          const widget = new (window as any).TradingView.widget({
            container: containerId,
            datafeed: createTopStepDatafeed(),
            library_path: '/charting_library/',
            symbol: `TOPSTEP:${chartContract}`,
            autosize: true,
            interval: '60',
            timezone: 'America/New_York',
            theme: 'Dark',
            locale: 'en',
            toolbar_bg: '#161B27',
            enable_publishing: false,
            hide_side_toolbar: false,
            allow_symbol_change: true,
            loading_screen: { backgroundColor: '#0d1117' },
            disabled_features: ['use_localstorage_for_settings', 'save_chart_properties_to_local_storage', 'header_saveload'],
          });
          tvRef.current = widget;
        } catch { /* noop */ }
      });
    };

    initChart();
    return () => {
      mounted = false;
      if (tvRef.current) { try { tvRef.current.remove(); } catch { /**/ } tvRef.current = null; }
    };
  }, [tab, chartContract]);

  // ── Handlers ────────────────────────────────────────────

  const handleConnect = async () => {
    setConnecting(true);
    setAuthError(null);
    try {
      const s = await topstepService.connect();
      setSession(s);
      if (s.authenticated) {
        await Promise.all([loadContracts(), loadSessionState()]);
      } else {
        setAuthError('Authentication failed — check PROJECTX_API_KEY in .env');
      }
    } catch (err: any) {
      setAuthError(err?.response?.data?.error || err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleResolveAccount = async () => {
    try {
      const acct = await topstepService.resolveAccount();
      setAccount(acct);
    } catch (err: any) {
      setAuthError(err?.response?.data?.error || err.message);
    }
  };

  const handleStartAutopilot = async () => {
    setApLoading(true);
    try {
      await topstepService.startAutopilot(apInstruments);
      const ap = await topstepService.getAutopilotStatus();
      setApStatus(ap);
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setApLoading(false);
    }
  };

  const handleStopAutopilot = async () => {
    setApLoading(true);
    try {
      await topstepService.stopAutopilot();
      const ap = await topstepService.getAutopilotStatus();
      setApStatus(ap);
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message);
    } finally {
      setApLoading(false);
    }
  };

  const handleKillSwitch = async () => {
    await topstepService.triggerKillSwitch('Manual override from Command Center');
    const [r, p] = await Promise.all([topstepService.getRisk(), topstepService.getChallengeProgress()]);
    setRisk(r); setProgress(p);
    setKillConfirm(false);
  };

  const handleClearKillSwitch = async () => {
    await topstepService.clearKillSwitch();
    const [r, p] = await Promise.all([topstepService.getRisk(), topstepService.getChallengeProgress()]);
    setRisk(r); setProgress(p);
    setClearConfirm(false);
  };

  const handlePlaceOrder = async () => {
    setPlacingOrder(true);
    setOrderError(null);
    try {
      await topstepService.placeOrder({
        contractSymbol: orderForm.symbol,
        side: orderForm.side as 'BUY' | 'SELL',
        contracts: orderForm.contracts,
        type: orderForm.type as 'market' | 'limit',
        limitPrice: orderForm.limitPrice ? parseFloat(orderForm.limitPrice) : undefined,
      });
      await loadOrders();
    } catch (err: any) {
      setOrderError(err?.response?.data?.error || err.message);
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleCancelOrder = async (orderId: number) => {
    await topstepService.cancelOrder(orderId);
    await loadOrders();
  };

  // ── Render helpers ──────────────────────────────────────

  const pnlColor = (n: number) => n > 0 ? COLORS.green : n < 0 ? COLORS.red : COLORS.muted;
  const isRunning = apStatus?.running ?? false;

  // ── Render ───────────────────────────────────────────────

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#0D1117', color: '#E5E4E2' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CurrencyExchange sx={{ color: COLORS.gold, fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.04em', color: COLORS.platinum }}>
              TopStep Command Center
            </Typography>
            {session?.dryRun && (
              <Chip label="DRY RUN" size="small" sx={{ bgcolor: 'rgba(255,167,38,0.15)', color: COLORS.orange, fontWeight: 700, fontSize: '0.65rem', letterSpacing: 1 }} />
            )}
            {session?.authenticated && (
              <Chip label="CONNECTED" size="small" icon={<CheckCircle sx={{ fontSize: '0.8rem !important' }} />}
                sx={{ bgcolor: 'rgba(102,187,106,0.12)', color: COLORS.green, fontWeight: 700, fontSize: '0.65rem' }} />
            )}
            {isRunning && (
              <Chip label="AUTOPILOT ACTIVE" size="small" icon={<MonitorHeart sx={{ fontSize: '0.8rem !important' }} />}
                sx={{ bgcolor: 'rgba(79,195,247,0.12)', color: COLORS.blue, fontWeight: 700, fontSize: '0.65rem', letterSpacing: 1 }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: COLORS.muted, mt: 0.5, display: 'block' }}>
            $50K Challenge · Target $3,000 · Max Loss $2,000 · {account?.name || '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadSessionState} size="small" sx={{ color: COLORS.muted, '&:hover': { color: COLORS.platinum } }}>
            <Refresh />
          </IconButton>
          {!session?.authenticated ? (
            <Button variant="contained" onClick={handleConnect} disabled={connecting} size="small"
              sx={{ bgcolor: COLORS.gold, color: '#000', fontWeight: 800, '&:hover': { bgcolor: '#C49B2F' } }}>
              {connecting ? <CircularProgress size={16} /> : 'Connect ProjectX'}
            </Button>
          ) : !account ? (
            <Button variant="outlined" onClick={handleResolveAccount} size="small"
              sx={{ borderColor: COLORS.blue, color: COLORS.blue, fontWeight: 700 }}>
              Resolve Account
            </Button>
          ) : null}
        </Box>
      </Box>

      {authError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAuthError(null)}>{authError}</Alert>}

      {/* Challenge Progress Bar — always visible once authenticated */}
      {progress && (
        <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.platinum }}>
              Challenge Progress
            </Typography>
            <Chip label={progress.postureLabel} size="small"
              sx={{ bgcolor: progress.postureLabel === 'DEFENSIVE' ? 'rgba(239,83,80,0.15)' : progress.postureLabel === 'AGGRESSIVE' ? 'rgba(102,187,106,0.15)' : 'rgba(255,255,255,0.08)',
                color: progress.postureLabel === 'DEFENSIVE' ? COLORS.red : progress.postureLabel === 'AGGRESSIVE' ? COLORS.green : COLORS.platinum,
                fontWeight: 700, fontSize: '0.65rem' }} />
          </Box>
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.5 }}>Profit Target</Typography>
              <LinearProgress variant="determinate" value={Math.min(progress.targetPct, 100)}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)', '& .MuiLinearProgress-bar': { bgcolor: COLORS.green } }} />
              <Typography variant="caption" sx={{ color: COLORS.green }}>{progress.targetPct.toFixed(1)}% of $3k</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.5 }}>Risk Used</Typography>
              <LinearProgress variant="determinate" value={Math.min(progress.riskUsedPct, 100)}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                  '& .MuiLinearProgress-bar': { bgcolor: progress.riskUsedPct > 80 ? COLORS.red : progress.riskUsedPct > 50 ? COLORS.orange : COLORS.blue } }} />
              <Typography variant="caption" sx={{ color: COLORS.orange }}>{progress.riskUsedPct.toFixed(1)}% of $1.7k stop</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.5 }}>P&L Summary</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: pnlColor(risk?.cumulativeRealizedPnl || 0) }}>
                {fmt$(risk?.cumulativeRealizedPnl || 0)} realized
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{fmt$(risk?.dailyRealizedPnl || 0)} today</Typography>
            </Grid>
          </Grid>
          {risk?.killSwitchActive && (
            <Alert severity="error" icon={<Lock />} sx={{ py: 0.5, fontSize: '0.8rem' }}>
              KILL SWITCH ACTIVE — {risk.killSwitchReason}
            </Alert>
          )}
        </Box>
      )}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 0, borderBottom: `1px solid ${COLORS.border}`,
          '& .MuiTab-root': { color: COLORS.muted, fontWeight: 600, fontSize: '0.8rem', minWidth: 110 },
          '& .Mui-selected': { color: COLORS.gold },
          '& .MuiTabs-indicator': { bgcolor: COLORS.gold } }}>
        <Tab label="Overview" icon={<MonitorHeart sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Autopilot" icon={<PlayArrow sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Kaze Signals" icon={<Air sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Chart" icon={<ShowChart sx={{ fontSize: '1rem' }} />} iconPosition="start" />
        <Tab label="Orders" icon={<Send sx={{ fontSize: '1rem' }} />} iconPosition="start" />
      </Tabs>

      {/* ── Tab 0: Overview ── */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={2.5}>
          {/* KPIs */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Account Balance" value={fmt$(account?.balance || 0)}
              sub={account?.canTrade ? 'Can trade' : 'Cannot trade'}
              color={account?.canTrade ? COLORS.green : COLORS.red} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Realized P&L" value={fmt$(risk?.cumulativeRealizedPnl || 0, 2)}
              sub={`Today: ${fmt$(risk?.dailyRealizedPnl || 0, 2)}`}
              color={pnlColor(risk?.cumulativeRealizedPnl || 0)} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Distance to Target" value={fmt$(risk?.distanceToTarget ?? CHALLENGE.target)}
              sub={`${pct((CHALLENGE.target - (risk?.distanceToTarget ?? CHALLENGE.target)), CHALLENGE.target)} complete`}
              color={COLORS.gold} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Distance to Stop" value={fmt$(risk?.distanceToInternalStop ?? CHALLENGE.internalStop)}
              sub={`Hard max: ${fmt$(CHALLENGE.hardMax)}`}
              color={(risk?.distanceToInternalStop ?? CHALLENGE.internalStop) < 500 ? COLORS.red : COLORS.platinum} />
          </Grid>

          {/* Account details */}
          <Grid item xs={12} md={5}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, height: '100%' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 2 }}>
                Account Details
              </Typography>
              {account ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    ['Account Name', account.name],
                    ['Account ID', account.id],
                    ['Balance', fmt$(account.balance)],
                    ['Can Trade', account.canTrade ? 'Yes' : 'No'],
                    ['Simulated', account.simulated ? 'Yes' : 'No'],
                    ['Session Status', session?.authenticated ? 'Authenticated' : 'Not authenticated'],
                    ['Token TTL', session ? `${Math.floor((session.expiresIn || 0) / 3600)}h` : '—'],
                    ['Mode', session?.dryRun ? 'DRY RUN' : 'LIVE'],
                  ].map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ color: COLORS.muted }}>{k}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.platinum }}>{v}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>
                  {session?.authenticated ? 'Click "Resolve Account" to load account details.' : 'Connect to ProjectX first.'}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Contracts */}
          <Grid item xs={12} md={7}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, height: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum }}>
                  Active Contracts
                </Typography>
                <Button size="small" onClick={loadContracts} startIcon={<Refresh sx={{ fontSize: '0.8rem' }} />}
                  sx={{ color: COLORS.muted, fontSize: '0.7rem' }}>
                  Reload
                </Button>
              </Box>
              {contracts.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Symbol', 'Name', 'Description', 'Tick Size', 'Tick Value', 'Chart'].map(h => (
                          <TableCell key={h} sx={{ color: COLORS.muted, fontSize: '0.7rem', borderBottom: `1px solid ${COLORS.border}`, pb: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contracts.map(c => {
                        const sym = (c.symbolId?.replace('F.US.', '') || c.name.replace(/[A-Z]\d+$/, '')).toUpperCase();
                        return (
                          <TableRow key={c.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                            <TableCell sx={{ color: COLORS.gold, fontWeight: 700, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{sym}</TableCell>
                            <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{c.name}</TableCell>
                            <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderBottom: `1px solid ${COLORS.border}` }}>{c.description}</TableCell>
                            <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{c.tickSize}</TableCell>
                            <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>${c.tickValue}</TableCell>
                            <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                              <Button size="small" onClick={() => { setChartContract(sym); setTab(3); }}
                                sx={{ color: COLORS.blue, fontSize: '0.7rem', p: 0.25, minWidth: 0 }}>
                                <ShowChart sx={{ fontSize: '0.9rem' }} />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>
                  {session?.authenticated ? 'No contracts loaded. Connect and resolve account first.' : 'Connect to ProjectX to load contracts.'}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Risk Engine */}
          <Grid item xs={12}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum }}>
                  Risk Engine State
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {risk?.killSwitchActive ? (
                    <Button size="small" variant="outlined" startIcon={<LockOpen />}
                      onClick={() => setClearConfirm(true)}
                      sx={{ borderColor: COLORS.green, color: COLORS.green, fontSize: '0.72rem' }}>
                      Clear Kill Switch
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" startIcon={<Lock />}
                      onClick={() => setKillConfirm(true)}
                      sx={{ borderColor: COLORS.red, color: COLORS.red, fontSize: '0.72rem' }}>
                      Kill Switch
                    </Button>
                  )}
                  <Button size="small" variant="outlined" onClick={async () => { const r = await topstepService.resetDaily(); setRisk(r); }}
                    sx={{ borderColor: COLORS.muted, color: COLORS.muted, fontSize: '0.72rem' }}>
                    Reset Daily
                  </Button>
                </Box>
              </Box>
              {risk ? (
                <Grid container spacing={2}>
                  {[
                    ['Total Trades', `${risk.totalTrades}`],
                    ['Wins / Losses', `${risk.wins}W / ${risk.losses}L`],
                    ['Win Rate', risk.totalTrades ? `${Math.round((risk.wins / risk.totalTrades) * 100)}%` : '—'],
                    ['Consec. Losses', `${risk.consecutiveLosses}`],
                    ['Daily Cap Reached', risk.dailyCapReached ? 'YES' : 'No'],
                    ['Kill Switch', risk.killSwitchActive ? 'ACTIVE' : 'Off'],
                    ['Posture', risk.posture.toUpperCase()],
                    ['Session Date', risk.sessionDate],
                  ].map(([k, v]) => (
                    <Grid item xs={6} sm={3} key={k}>
                      <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>{k}</Typography>
                      <Typography variant="body2" sx={{
                        fontWeight: 700,
                        color: k === 'Kill Switch' && v === 'ACTIVE' ? COLORS.red
                          : k === 'Daily Cap Reached' && v === 'YES' ? COLORS.orange
                          : k === 'Posture' && v === 'DEFENSIVE' ? COLORS.red
                          : COLORS.platinum
                      }}>{v}</Typography>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>Connect and authenticate to load risk state.</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── Tab 1: Autopilot ── */}
      <TabPanel value={tab} index={1}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 2 }}>
                Autonomous Trading
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted, mb: 2, lineHeight: 1.7 }}>
                Tomi evaluates market conditions via Kaze directional scoring. Signals passing the risk engine are submitted as futures orders on ProjectX.
              </Typography>
              {/* Multi-instrument selector */}
              {apInstrumentGroups.map(group => (
                <Box key={group.label} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                    {group.label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {group.items.map((instrument) => {
                      const active = apInstruments.includes(instrument.symbol);
                      return (
                        <Tooltip key={instrument.symbol} title={instrument.fullName} placement="top">
                          <Chip
                            label={instrument.symbol}
                            size="small"
                            onClick={() => {
                              if (isRunning) return;
                              setApInstruments(prev =>
                                prev.includes(instrument.symbol) ? prev.filter(s => s !== instrument.symbol) : [...prev, instrument.symbol]
                              );
                            }}
                            sx={{
                              cursor: isRunning ? 'default' : 'pointer',
                              fontWeight: 700, fontSize: '0.65rem', height: 22,
                              bgcolor: active ? 'rgba(79,195,247,0.15)' : 'rgba(255,255,255,0.05)',
                              color: active ? COLORS.blue : COLORS.muted,
                              border: active ? `1px solid ${COLORS.blue}40` : `1px solid transparent`,
                              '&:hover': isRunning ? {} : { bgcolor: active ? 'rgba(79,195,247,0.22)' : 'rgba(255,255,255,0.09)' },
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.68rem', display: 'block', mb: 1.5 }}>
                {apInstruments.length} instrument{apInstruments.length !== 1 ? 's' : ''} selected · Tomi picks highest-conviction signal each cycle
              </Typography>
              {isRunning ? (
                <Button fullWidth variant="contained" onClick={handleStopAutopilot} disabled={apLoading}
                  startIcon={apLoading ? <CircularProgress size={14} /> : <Stop />}
                  sx={{ bgcolor: COLORS.red, '&:hover': { bgcolor: '#C62828' }, fontWeight: 800 }}>
                  Stop Autopilot
                </Button>
              ) : (
                <Button fullWidth variant="contained" onClick={handleStartAutopilot}
                  disabled={apLoading || !session?.authenticated || !account || apInstruments.length === 0}
                  startIcon={apLoading ? <CircularProgress size={14} /> : <PlayArrow />}
                  sx={{ bgcolor: COLORS.green, color: '#000', '&:hover': { bgcolor: '#43A047' }, fontWeight: 800 }}>
                  Launch Tomi ({apInstruments.length} instruments)
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 1.5 }}>
                Session Status
              </Typography>
              {apStatus?.session ? (
                <>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    {[
                      ['Status', apStatus.session.status.toUpperCase(), apStatus.session.status === 'running' ? COLORS.green : COLORS.muted],
                      ['Instruments', (apStatus.session.instrumentList?.join(', ') || apStatus.session.primaryInstrument), COLORS.gold],
                      ['Cycles', `${apStatus.session.cycles}`, COLORS.platinum],
                      ['Trades Executed', `${apStatus.session.stats.tradesExecuted}`, COLORS.platinum],
                      ['Risk Rejected', `${apStatus.session.stats.tradesRejectedByRisk}`, COLORS.orange],
                      ['Errors', `${apStatus.session.stats.errors}`, COLORS.platinum],
                    ].map(([k, v, color]) => (
                      <Grid item xs={4} sm={2} key={k}>
                        <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', fontSize: '0.65rem', textTransform: 'uppercase' }}>{k}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: color as string }}>{v}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 1 }}>
                    Last scan: {apStatus.session.lastScan ? new Date(apStatus.session.lastScan).toLocaleTimeString() : '—'}
                    {apStatus.session.nextScan ? ` · Next: ${new Date(apStatus.session.nextScan).toLocaleTimeString()}` : ''}
                  </Typography>
                  <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />
                  <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
                    Recent Decisions
                  </Typography>
                  <Box sx={{ maxHeight: 260, overflowY: 'auto', mt: 1 }}>
                    {apStatus.session.decisions.slice().reverse().map(d => (
                      <Box key={d.id} sx={{ py: 1, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Box sx={{ display: 'flex', gap: 0.75, mb: 0.25 }}>
                            <Chip label={d.type.toUpperCase()} size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(255,255,255,0.07)', color: COLORS.muted }} />
                            <Chip label={d.action} size="small"
                              sx={{ fontSize: '0.6rem', height: 18,
                                bgcolor: d.action === 'BUY' ? 'rgba(102,187,106,0.15)' : d.action === 'SELL' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.07)',
                                color: d.action === 'BUY' ? COLORS.green : d.action === 'SELL' ? COLORS.red : COLORS.muted }} />
                            {d.executed && <Chip label="EXECUTED" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(79,195,247,0.12)', color: COLORS.blue }} />}
                          </Box>
                          <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.7rem' }}>{d.reason}</Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.65rem', whiteSpace: 'nowrap', ml: 1 }}>
                          {new Date(d.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    ))}
                    {apStatus.session.decisions.length === 0 && (
                      <Typography variant="body2" sx={{ color: COLORS.muted, py: 1 }}>No decisions yet — waiting for next scan cycle.</Typography>
                    )}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>No active session. Launch Tomi to begin.</Typography>
              )}
            </Box>
          </Grid>

          {/* ── Performance Tracker ── */}
          <Grid item xs={12}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 2 }}>
                Portfolio Performance
              </Typography>

              {apStatus?.risk ? (
                <>
                  {/* P&L Stat Row */}
                  <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                    <Grid item xs={6} sm={3}>
                      <StatCard label="Daily P&L" value={fmt$(apStatus.risk.dailyRealizedPnl)}
                        color={apStatus.risk.dailyRealizedPnl >= 0 ? COLORS.green : COLORS.red} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatCard label="Cumulative P&L" value={fmt$(apStatus.risk.cumulativeRealizedPnl)}
                        color={apStatus.risk.cumulativeRealizedPnl >= 0 ? COLORS.green : COLORS.red} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatCard label="Unrealized" value={fmt$(apStatus.risk.cumulativeUnrealizedPnl)}
                        color={apStatus.risk.cumulativeUnrealizedPnl >= 0 ? COLORS.green : COLORS.red} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatCard
                        label="Win Rate"
                        value={apStatus.risk.totalTrades ? `${Math.round((apStatus.risk.wins / apStatus.risk.totalTrades) * 100)}%` : '—'}
                        sub={`${apStatus.risk.wins}W / ${apStatus.risk.losses}L · ${apStatus.risk.totalTrades} trades`}
                        color={apStatus.risk.totalTrades && apStatus.risk.wins / apStatus.risk.totalTrades >= 0.5 ? COLORS.green : COLORS.orange}
                      />
                    </Grid>
                  </Grid>

                  {/* Challenge Progress Bars */}
                  <Grid container spacing={2} sx={{ mb: 2.5 }}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                        Distance to Target
                      </Typography>
                      <LinearProgress variant="determinate"
                        value={Math.min(100, Math.max(0, (1 - apStatus.risk.distanceToTarget / CHALLENGE.target) * 100))}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { bgcolor: COLORS.gold, borderRadius: 4 } }} />
                      <Typography variant="caption" sx={{ color: COLORS.gold, fontWeight: 700, mt: 0.5, display: 'block' }}>
                        {fmt$(apStatus.risk.distanceToTarget)} remaining · {fmt$(CHALLENGE.target)} target
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                        Internal Stop Buffer
                      </Typography>
                      <LinearProgress variant="determinate"
                        value={Math.min(100, Math.max(0, (apStatus.risk.distanceToInternalStop / CHALLENGE.internalStop) * 100))}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { bgcolor: apStatus.risk.distanceToInternalStop < 500 ? COLORS.red : COLORS.orange, borderRadius: 4 } }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.5, display: 'block',
                        color: apStatus.risk.distanceToInternalStop < 500 ? COLORS.red : COLORS.orange }}>
                        {fmt$(apStatus.risk.distanceToInternalStop)} buffer remaining
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.8, display: 'block', mb: 0.5 }}>
                        Hard Max Buffer
                      </Typography>
                      <LinearProgress variant="determinate"
                        value={Math.min(100, Math.max(0, (apStatus.risk.distanceToHardMax / CHALLENGE.hardMax) * 100))}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)',
                          '& .MuiLinearProgress-bar': { bgcolor: apStatus.risk.distanceToHardMax < 300 ? COLORS.red : COLORS.platinum, borderRadius: 4 } }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, mt: 0.5, display: 'block',
                        color: apStatus.risk.distanceToHardMax < 300 ? COLORS.red : COLORS.muted }}>
                        {fmt$(apStatus.risk.distanceToHardMax)} buffer remaining
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Equity Curve */}
                  {(apStatus.risk.tradeLog?.filter(t => t.status === 'closed').length ?? 0) >= 2 && (
                    <>
                      <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                        Equity Curve
                      </Typography>
                      <Box sx={{ mb: 2.5, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${COLORS.border}`, bgcolor: COLORS.surface2, px: 1, pt: 1 }}>
                        <EquityCurve trades={apStatus.risk.tradeLog} />
                      </Box>
                    </>
                  )}

                  {/* Fibonacci Recovery Ladder */}
                  {fibLadderPositions.length > 0 && (
                    <Box sx={{ mb: 2.5, borderRadius: 2, overflow: 'hidden', border: `1px solid ${COLORS.border}`, bgcolor: COLORS.surface2, p: 2 }}>
                      <FibonacciLadderViz
                        positions={fibLadderPositions}
                        takeProfitTarget={600}
                        stopLossFloor={-200}
                        accountLabel="Express Funded"
                      />
                    </Box>
                  )}

                  {/* Trade Log */}
                  <Divider sx={{ borderColor: COLORS.border, mb: 1.5 }} />
                  <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 1 }}>
                    Trade Log
                  </Typography>
                  {apStatus.risk.tradeLog?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {['Symbol', 'Side', 'Qty', 'Entry', 'Exit', 'P&L', 'Opened', 'Closed'].map(h => (
                              <TableCell key={h} sx={{ color: COLORS.muted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.8, borderColor: COLORS.border, py: 0.75, px: 1 }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...apStatus.risk.tradeLog].reverse().slice(0, 15).map(t => (
                            <TableRow key={t.id} sx={{ '&:last-child td': { border: 0 } }}>
                              <TableCell sx={{ color: COLORS.platinum, fontWeight: 700, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.symbol}</TableCell>
                              <TableCell sx={{ borderColor: COLORS.border, py: 0.75, px: 1 }}>
                                <Chip label={t.side} size="small" sx={{ fontSize: '0.6rem', height: 18,
                                  bgcolor: t.side === 'LONG' ? 'rgba(102,187,106,0.15)' : 'rgba(239,83,80,0.15)',
                                  color: t.side === 'LONG' ? COLORS.green : COLORS.red }} />
                              </TableCell>
                              <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.contracts}</TableCell>
                              <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.entryPrice > 0 ? t.entryPrice.toFixed(4) : '—'}</TableCell>
                              <TableCell sx={{ color: COLORS.muted, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>{t.exitPrice != null ? t.exitPrice.toFixed(4) : '—'}</TableCell>
                              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', borderColor: COLORS.border, py: 0.75, px: 1,
                                color: t.realizedPnl == null ? COLORS.muted : t.realizedPnl >= 0 ? COLORS.green : COLORS.red }}>
                                {t.realizedPnl != null ? fmt$(t.realizedPnl, 2) : t.status === 'open'
                                  ? <Chip label="OPEN" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(79,195,247,0.15)', color: COLORS.blue }} />
                                  : '—'}
                              </TableCell>
                              <TableCell sx={{ color: COLORS.muted, fontSize: '0.68rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>
                                {t.timestamp ? new Date(t.timestamp).toLocaleTimeString() : '—'}
                              </TableCell>
                              <TableCell sx={{ color: COLORS.muted, fontSize: '0.68rem', borderColor: COLORS.border, py: 0.75, px: 1 }}>
                                {t.closedAt ? new Date(t.closedAt).toLocaleTimeString() : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: COLORS.muted, py: 1 }}>No trades recorded yet.</Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>Start the autopilot session to track performance.</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── Tab 2: Kaze Signals ── */}
      <TabPanel value={tab} index={2}>
        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum }}>
            Kaze Futures Intelligence
          </Typography>
          <Button size="small" startIcon={<Refresh sx={{ fontSize: '0.8rem' }} />}
            onClick={loadKazeSignals} disabled={kazeLoading}
            sx={{ color: COLORS.muted }}>
            Refresh Signals
          </Button>
        </Box>

        {kazeLoading && <LinearProgress sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: COLORS.gold } }} />}

        {/* Macro Meta Bar */}
        {kazeMeta && (
          <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`,
            display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>Macro Regime</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.platinum, fontSize: '0.82rem' }}>{kazeMeta.macroRegime}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>Trend</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.platinum, fontSize: '0.82rem' }}>{kazeMeta.macroTrend}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>VIX</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700,
                color: kazeMeta.vixLevel >= 30 ? COLORS.red : kazeMeta.vixLevel >= 20 ? COLORS.orange : COLORS.green,
                fontSize: '0.82rem' }}>
                {kazeMeta.vixLevel.toFixed(1)} <span style={{ color: COLORS.muted, fontWeight: 400, fontSize: '0.7rem' }}>({kazeMeta.vixRegime})</span>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>Global Sentiment</Typography>
              <Chip label={kazeMeta.globalSentiment} size="small"
                sx={{ mt: 0.25, height: 20, fontSize: '0.65rem', fontWeight: 800,
                  bgcolor: kazeMeta.globalSentiment === 'BULLISH' ? 'rgba(102,187,106,0.15)' : kazeMeta.globalSentiment === 'BEARISH' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.07)',
                  color: kazeMeta.globalSentiment === 'BULLISH' ? COLORS.green : kazeMeta.globalSentiment === 'BEARISH' ? COLORS.red : COLORS.muted }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>Risk Posture</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700,
                color: kazeMeta.riskPosture === 'RISK-OFF' ? COLORS.red : kazeMeta.riskPosture === 'RISK-ON' ? COLORS.green : COLORS.orange,
                fontSize: '0.82rem' }}>{kazeMeta.riskPosture}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>Liquidity</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: COLORS.platinum, fontSize: '0.82rem' }}>{kazeMeta.liquidityState}</Typography>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.63rem' }}>
                {kazeSignals.length} contracts · {new Date(kazeMeta.generatedAt).toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Category filter chips */}
        {kazeSignals.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
            {(['all', 'equity_index', 'commodity', 'fx', 'rates'] as const).map(cat => {
              const count = cat === 'all' ? kazeSignals.length : kazeSignals.filter(s => s.category === cat).length;
              const label = cat === 'all' ? 'All' : cat === 'equity_index' ? 'Equity Index' : cat === 'commodity' ? 'Commodity' : cat === 'fx' ? 'FX' : 'Rates';
              return (
                <Chip key={cat} label={`${label} (${count})`} size="small"
                  onClick={() => setCategoryFilter(cat)}
                  sx={{
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.7rem',
                    bgcolor: categoryFilter === cat ? COLORS.gold : 'rgba(255,255,255,0.07)',
                    color: categoryFilter === cat ? '#000' : COLORS.muted,
                    '&:hover': { bgcolor: categoryFilter === cat ? COLORS.gold : 'rgba(255,255,255,0.12)' },
                  }} />
              );
            })}
          </Box>
        )}

        <Grid container spacing={2.5}>
          {kazeSignals
            .filter(s => categoryFilter === 'all' || s.category === categoryFilter)
            .map(sig => (
            <Grid item xs={12} md={6} key={sig.contractId}>
              <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderLeft: `3px solid ${directionColor(sig.direction)}` }}>

                {/* Card header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: COLORS.gold, lineHeight: 1 }}>{sig.symbol}</Typography>
                      <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.68rem' }}>
                        {sig.category === 'equity_index' ? 'Equity Index' : sig.category === 'commodity' ? 'Commodity' : sig.category === 'fx' ? 'FX' : 'Rates'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>{sig.contractName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Chip label={sig.direction} size="small"
                      sx={{ bgcolor: sig.direction === 'LONG' ? 'rgba(102,187,106,0.15)' : sig.direction === 'SHORT' ? 'rgba(239,83,80,0.15)' : 'rgba(255,255,255,0.07)',
                        color: directionColor(sig.direction), fontWeight: 800, fontSize: '0.65rem' }} />
                    <Chip label={sig.confidence} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.07)', color: sig.confidence === 'HIGH' ? COLORS.green : sig.confidence === 'MEDIUM' ? COLORS.orange : COLORS.muted,
                        fontWeight: 700, fontSize: '0.65rem' }} />
                    <Chip label={sig.sentiment} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.07)', color: sig.sentiment === 'BULLISH' ? COLORS.green : sig.sentiment === 'BEARISH' ? COLORS.red : COLORS.muted,
                        fontWeight: 700, fontSize: '0.65rem' }} />
                  </Box>
                </Box>

                {/* Directional score */}
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>Directional Score</Typography>
                    <Typography variant="caption" sx={{ color: COLORS.muted }}>Tomi {(sig.tomiComposite * 100).toFixed(0)}%</Typography>
                  </Box>
                  {scoreBar(sig.directionalScore)}
                </Box>

                {/* Proxy equity K-scores */}
                {sig.proxyScores && sig.proxyScores.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.6, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8 }}>
                      Proxy Equity Signals
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {sig.proxyScores.slice(0, 5).map(ps => (
                        <Box key={ps.ticker} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.68rem', minWidth: 44, fontWeight: 600 }}>{ps.ticker}</Typography>
                          <LinearProgress variant="determinate" value={Math.round(ps.k_score * 100)}
                            sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: ps.k_score >= 0.7 ? COLORS.green : ps.k_score <= 0.3 ? COLORS.red : COLORS.orange,
                                borderRadius: 3,
                              } }} />
                          <Typography variant="caption" sx={{
                            color: ps.k_score >= 0.7 ? COLORS.green : ps.k_score <= 0.3 ? COLORS.red : COLORS.orange,
                            fontWeight: 700, fontSize: '0.65rem', minWidth: 32, textAlign: 'right',
                          }}>
                            {Math.round(ps.k_score * 100)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Long / short drivers */}
                {(sig.longDrivers?.length > 0 || sig.shortDrivers?.length > 0) && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.6, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8 }}>
                      Drivers
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {sig.longDrivers?.slice(0, 3).map((d, i) => (
                        <Chip key={`L${i}`} label={d} size="small"
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(102,187,106,0.10)', color: COLORS.green, fontWeight: 600 }} />
                      ))}
                      {sig.shortDrivers?.slice(0, 3).map((d, i) => (
                        <Chip key={`S${i}`} label={d} size="small"
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'rgba(239,83,80,0.10)', color: COLORS.red, fontWeight: 600 }} />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Signal rationale */}
                <Box sx={{ mb: sig.briefingExcerpt ? 1.5 : 0 }}>
                  <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.25 }}>Rationale</Typography>
                  <Typography variant="body2" sx={{ color: COLORS.platinum, fontSize: '0.75rem', lineHeight: 1.6 }}>{sig.reason}</Typography>
                </Box>

                {/* Briefing excerpt */}
                {sig.briefingExcerpt && (
                  <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderLeft: `2px solid ${COLORS.gold}40` }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mb: 0.25, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      Intelligence Briefing
                    </Typography>
                    <Typography variant="caption" sx={{ color: COLORS.platinum, fontSize: '0.72rem', lineHeight: 1.6, display: 'block' }}>
                      {sig.briefingExcerpt}
                    </Typography>
                  </Box>
                )}

                {/* Top news */}
                {sig.topNews.length > 0 && (
                  <>
                    <Divider sx={{ borderColor: COLORS.border, mb: 1 }} />
                    <Typography variant="caption" sx={{ color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.6rem', display: 'block', mb: 0.75 }}>
                      Driving News
                    </Typography>
                    {sig.topNews.slice(0, 3).map((n, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, minWidth: 36 }}>
                          <Chip label={n.impact} size="small"
                            sx={{ fontSize: '0.55rem', height: 16,
                              bgcolor: n.impact === 'HIGH' ? 'rgba(239,83,80,0.15)' : n.impact === 'MEDIUM' ? 'rgba(255,167,38,0.15)' : 'rgba(255,255,255,0.07)',
                              color: n.impact === 'HIGH' ? COLORS.red : n.impact === 'MEDIUM' ? COLORS.orange : COLORS.muted }} />
                          <Chip label={n.direction === 'BULLISH' ? '↑' : n.direction === 'BEARISH' ? '↓' : '–'} size="small"
                            sx={{ fontSize: '0.6rem', height: 16,
                              bgcolor: n.direction === 'BULLISH' ? 'rgba(102,187,106,0.10)' : n.direction === 'BEARISH' ? 'rgba(239,83,80,0.10)' : 'rgba(255,255,255,0.05)',
                              color: n.direction === 'BULLISH' ? COLORS.green : n.direction === 'BEARISH' ? COLORS.red : COLORS.muted }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" sx={{ color: COLORS.platinum, fontSize: '0.72rem', lineHeight: 1.4, display: 'block' }}>{n.headline}</Typography>
                          <Typography variant="caption" sx={{ color: COLORS.muted, fontSize: '0.63rem' }}>
                            {n.source} · K-score {(n.kaze_score * 100).toFixed(0)} · {new Date(n.timestamp).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </>
                )}

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                  <Button size="small" onClick={() => { setChartContract(sig.symbol); setTab(3); }}
                    startIcon={<ShowChart sx={{ fontSize: '0.8rem' }} />}
                    sx={{ color: COLORS.blue, fontSize: '0.7rem', border: `1px solid ${COLORS.blue}30` }}>
                    Chart
                  </Button>
                  {sig.direction !== 'FLAT' && (
                    <Button size="small" onClick={() => {
                      setOrderForm(f => ({ ...f, symbol: sig.symbol, side: sig.direction === 'LONG' ? 'BUY' : 'SELL' }));
                      setTab(4);
                    }}
                    sx={{ color: directionColor(sig.direction), fontSize: '0.7rem',
                      border: `1px solid ${directionColor(sig.direction)}30` }}>
                      {sig.direction === 'LONG' ? 'BUY' : 'SELL'} {sig.symbol}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}

          {kazeSignals.length === 0 && !kazeLoading && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>
                {contracts.length === 0 ? 'Load contracts first (Overview tab → Connect ProjectX).' : 'Generating signals — refresh to check.'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* ── Tab 3: Chart ── */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {contracts.map(c => {
            const sym = normalizeTopStepSymbolKey(c.name || c.symbolId);
            return (
              <Button key={c.id} size="small" variant={chartContract === sym ? 'contained' : 'outlined'}
                onClick={() => setChartContract(sym)}
                sx={{
                  ...(chartContract === sym
                    ? { bgcolor: COLORS.gold, color: '#000', '&:hover': { bgcolor: '#C49B2F' } }
                    : { borderColor: COLORS.border, color: COLORS.muted }),
                  fontWeight: 700, fontSize: '0.75rem',
                }}>
                {sym} — {c.name}
              </Button>
            );
          })}
          {contracts.length === 0 && (
            <Typography variant="body2" sx={{ color: COLORS.muted }}>Connect to ProjectX to load contracts and their charts.</Typography>
          )}
        </Box>
        <Box ref={chartRef} sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${COLORS.border}`, minHeight: 540,
          bgcolor: COLORS.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!chartContract && (
            <Typography variant="body2" sx={{ color: COLORS.muted }}>Select a contract above to load its chart.</Typography>
          )}
          {chartContract && contracts.length === 0 && (
            <Typography variant="body2" sx={{ color: COLORS.muted }}>TradingView chart loading…</Typography>
          )}
        </Box>
      </TabPanel>

      {/* ── Tab 4: Orders ── */}
      <TabPanel value={tab} index={4}>
        <Grid container spacing={2.5}>
          {/* Manual order entry */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 2 }}>
                Manual Order
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.orange, display: 'block', mb: 1.5, fontSize: '0.72rem' }}>
                All orders pass through the risk engine veto.
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ color: COLORS.muted }}>Symbol</InputLabel>
                <Select value={orderForm.symbol} label="Symbol" onChange={e => setOrderForm(f => ({ ...f, symbol: e.target.value }))}
                  sx={{ color: COLORS.platinum, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                  {contracts.map(c => {
                    const sym = (c.symbolId?.replace('F.US.', '') || c.name.replace(/[A-Z]\d+$/, '')).toUpperCase();
                    return <MenuItem key={c.id} value={sym}>{sym} — {c.description}</MenuItem>;
                  })}
                  {contracts.length === 0 && <MenuItem value="MNQ">MNQ (load contracts first)</MenuItem>}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ color: COLORS.muted }}>Side</InputLabel>
                <Select value={orderForm.side} label="Side" onChange={e => setOrderForm(f => ({ ...f, side: e.target.value }))}
                  sx={{ color: orderForm.side === 'BUY' ? COLORS.green : COLORS.red, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                  <MenuItem value="BUY">BUY (Long)</MenuItem>
                  <MenuItem value="SELL">SELL (Short)</MenuItem>
                </Select>
              </FormControl>

              <TextField fullWidth size="small" label="Contracts" type="number"
                value={orderForm.contracts} onChange={e => setOrderForm(f => ({ ...f, contracts: parseInt(e.target.value) || 1 }))}
                inputProps={{ min: 1, max: 10 }}
                sx={{ mb: 1.5, input: { color: COLORS.platinum }, label: { color: COLORS.muted }, '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }} />

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel sx={{ color: COLORS.muted }}>Order Type</InputLabel>
                <Select value={orderForm.type} label="Order Type" onChange={e => setOrderForm(f => ({ ...f, type: e.target.value }))}
                  sx={{ color: COLORS.platinum, '.MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }}>
                  <MenuItem value="market">Market</MenuItem>
                  <MenuItem value="limit">Limit</MenuItem>
                </Select>
              </FormControl>

              {orderForm.type === 'limit' && (
                <TextField fullWidth size="small" label="Limit Price" type="number"
                  value={orderForm.limitPrice} onChange={e => setOrderForm(f => ({ ...f, limitPrice: e.target.value }))}
                  sx={{ mb: 1.5, input: { color: COLORS.platinum }, label: { color: COLORS.muted }, '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border } }} />
              )}

              {orderError && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.78rem' }} onClose={() => setOrderError(null)}>{orderError}</Alert>}

              <Button fullWidth variant="contained" onClick={handlePlaceOrder}
                disabled={placingOrder || !session?.authenticated || risk?.killSwitchActive}
                startIcon={placingOrder ? <CircularProgress size={14} /> : <Send />}
                sx={{ bgcolor: orderForm.side === 'BUY' ? COLORS.green : COLORS.red, color: '#fff',
                  '&:hover': { bgcolor: orderForm.side === 'BUY' ? '#43A047' : '#C62828' }, fontWeight: 800 }}>
                {placingOrder ? 'Placing…' : `${orderForm.side} ${orderForm.contracts} ${orderForm.symbol}`}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            {/* Open positions */}
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum }}>Open Positions</Typography>
                <IconButton size="small" onClick={loadOrders} sx={{ color: COLORS.muted }}><Refresh sx={{ fontSize: '0.9rem' }} /></IconButton>
              </Box>
              {positions.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Contract', 'Side', 'Size', 'Avg Entry', 'Last Price', 'Unrealized P&L', ''].map(h => (
                          <TableCell key={h} sx={{ color: COLORS.muted, fontSize: '0.7rem', borderBottom: `1px solid ${COLORS.border}`, pb: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {positions.map((p, i) => (
                        <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell sx={{ color: COLORS.gold, fontWeight: 700, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{p.contractName || p.contractId}</TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <Chip label={p.side === 0 ? 'LONG' : 'SHORT'} size="small"
                              sx={{ fontSize: '0.65rem', height: 18, bgcolor: p.side === 0 ? 'rgba(102,187,106,0.15)' : 'rgba(239,83,80,0.15)',
                                color: p.side === 0 ? COLORS.green : COLORS.red, fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{p.size}</TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{fmtPrice(p.avgEntryPrice)}</TableCell>
                          <TableCell sx={{ color: COLORS.blue, fontSize: '0.8rem', borderBottom: `1px solid ${COLORS.border}` }}>{p.currentPrice ? fmtPrice(p.currentPrice) : '—'}</TableCell>
                          <TableCell sx={{ color: p.pnlIsRealtime === false ? COLORS.muted : pnlColor(p.unrealizedPnl), fontSize: '0.8rem', fontWeight: 700, borderBottom: `1px solid ${COLORS.border}` }}>
                            {p.pnlIsRealtime === false ? '—' : fmt$(p.unrealizedPnl, 2)}
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <Tooltip title={`Close ${p.size}x ${p.contractName || p.contractId} at market`}>
                              <Button size="small" variant="outlined"
                                onClick={async () => {
                                  if (!window.confirm(`Close ${p.size}x ${p.contractName || p.contractId} at market?`)) return;
                                  const result = await topstepService.closePosition(p.contractId, p.size);
                                  if (result.success) { loadOrders(); loadSessionState(); }
                                  else alert(`Close failed: ${result.message}`);
                                }}
                                sx={{ fontSize: '0.6rem', py: 0.25, px: 1, color: COLORS.red, borderColor: COLORS.red,
                                  '&:hover': { bgcolor: 'rgba(239,83,80,0.1)' } }}>
                                Close
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>No open positions.</Typography>
              )}
            </Box>

            {/* Order history */}
            <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: COLORS.platinum, mb: 1.5 }}>Order History</Typography>
              {orders.length > 0 ? (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['ID', 'Contract', 'Type', 'Side', 'Size', 'Price', 'Status', 'Time', ''].map(h => (
                          <TableCell key={h} sx={{ color: COLORS.muted, fontSize: '0.7rem', bgcolor: COLORS.surface2, borderBottom: `1px solid ${COLORS.border}`, pb: 0.5 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.slice().reverse().map(o => (
                        <TableRow key={o.id} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.72rem', borderBottom: `1px solid ${COLORS.border}` }}>{o.id}</TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.72rem', borderBottom: `1px solid ${COLORS.border}` }}>{o.contractId}</TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.72rem', borderBottom: `1px solid ${COLORS.border}` }}>{orderTypeLabel(o.type)}</TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <Chip label={orderSideLabel(o.side)} size="small"
                              sx={{ fontSize: '0.6rem', height: 16, bgcolor: o.side === 0 ? 'rgba(102,187,106,0.15)' : 'rgba(239,83,80,0.15)',
                                color: o.side === 0 ? COLORS.green : COLORS.red, fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.72rem', borderBottom: `1px solid ${COLORS.border}` }}>{o.filledSize}/{o.size}</TableCell>
                          <TableCell sx={{ color: COLORS.platinum, fontSize: '0.72rem', borderBottom: `1px solid ${COLORS.border}` }}>
                            {fmtPrice(o.avgFillPrice ?? o.limitPrice)}
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <Chip label={statusLabel(o.status)} size="small"
                              sx={{ fontSize: '0.6rem', height: 16, bgcolor: `${statusColor(o.status)}18`, color: statusColor(o.status), fontWeight: 700 }} />
                          </TableCell>
                          <TableCell sx={{ color: COLORS.muted, fontSize: '0.68rem', borderBottom: `1px solid ${COLORS.border}` }}>
                            {new Date(o.createdAt).toLocaleTimeString()}
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            {(o.status === 0 || o.status === 1) && (
                              <Tooltip title="Cancel order">
                                <IconButton size="small" onClick={() => handleCancelOrder(o.id)}
                                  sx={{ color: COLORS.red, p: 0.25 }}>
                                  <Cancel sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" sx={{ color: COLORS.muted }}>No orders yet.</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Kill Switch Confirm Dialog */}
      <Dialog open={killConfirm} onClose={() => setKillConfirm(false)}
        PaperProps={{ sx: { bgcolor: COLORS.surface2, border: `1px solid ${COLORS.border}` } }}>
        <DialogTitle sx={{ color: COLORS.red, fontWeight: 800 }}>Trigger Kill Switch?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: COLORS.platinum }}>
            This will immediately halt all trading and lock the system. The autopilot will stop.
            No new orders can be placed until the kill switch is cleared by an admin.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillConfirm(false)} sx={{ color: COLORS.muted }}>Cancel</Button>
          <Button onClick={handleKillSwitch} variant="contained" sx={{ bgcolor: COLORS.red, '&:hover': { bgcolor: '#C62828' }, fontWeight: 800 }}>
            Trigger Kill Switch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Kill Switch Confirm Dialog */}
      <Dialog open={clearConfirm} onClose={() => setClearConfirm(false)}
        PaperProps={{ sx: { bgcolor: COLORS.surface2, border: `1px solid ${COLORS.border}` } }}>
        <DialogTitle sx={{ color: COLORS.green, fontWeight: 800 }}>Clear Kill Switch?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: COLORS.platinum }}>
            This will re-enable trading. Verify that all risk conditions are safe before proceeding.
            The reason for the kill switch: <strong>{risk?.killSwitchReason || 'None recorded'}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirm(false)} sx={{ color: COLORS.muted }}>Cancel</Button>
          <Button onClick={handleClearKillSwitch} variant="contained" sx={{ bgcolor: COLORS.green, color: '#000', fontWeight: 800 }}>
            Clear Kill Switch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
